/**
 * BSS B2B checkout guard
 * 1) Adds a timeout to the BSS fetch helper, so a hanging /checkout/create
 *    request fails and the app's own catch-block redirects to /checkout.
 * 2) Watchdog: if the user clicks checkout and nothing happens, a
 *    "BSS checkout stuck" event with diagnostics is sent to Sentry.
 */


(() => {
  const SLOW_WARNING_MS = 2000; // Sentry warning if the BSS request is still pending
  const FETCH_TIMEOUT_MS = 5000; // abort (draft orders may legitimately take a few seconds)
  const WATCHDOG_MS = 7000; // Sentry error if the user is still on the page

  const diag = { requests: [], sent: {} };

  function report(message, level, data) {
    if (diag.sent[message] || !window.Sentry) return;
    diag.sent[message] = true; // max. one event per message and page view
    window.Sentry.captureMessage(message, {
      level,
      fingerprint: ["bss-checkout-guard", message],
      tags: { feature: "bss-checkout" },
      extra: data,
    });
  }

  // 1) Timeout patch: poll until the BSS app has defined window.requestFetch.
  let tries = 0;
  const patchInterval = setInterval(() => {
    if (++tries > 75) return clearInterval(patchInterval);
    if (typeof window.requestFetch !== "function") return;
    clearInterval(patchInterval);

    const original = window.requestFetch;
    window.requestFetch = (url, options) => {
      if (!String(url).includes("/checkout/")) return original(url, options);

      const entry = { url: String(url), start: Date.now(), state: "pending" };
      diag.requests.push(entry);

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      const warnTimer = setTimeout(() => {
        if (entry.state === "pending") {
          report("BSS-Warning: Checkout not loading immediately", "warning", {
            pendingForMs: Date.now() - entry.start,
            url: entry.url,
            customerId: window.__st?.cid ?? null,
          });
        }
      }, SLOW_WARNING_MS);

      return original(url, { ...options, signal: controller.signal })
        .then((res) => {
          Object.assign(entry, { state: "done", success: res?.success, errCode: res?.errCode });
          return res;
        })
        .catch((err) => {
          entry.state = controller.signal.aborted ? "timeout" : "failed";
          entry.error = err?.status || err?.message || String(err);
          throw err; // lets the BSS catch-block redirect to /checkout
        })
        .finally(() => {
          clearTimeout(timer);
          clearTimeout(warnTimer);
          entry.ms = Date.now() - entry.start;
        });
    };
  }, 200);

  // 2) Watchdog on checkout clicks (capture phase, BSS stops propagation).
  document.addEventListener(
    "click",
    (e) => {
      if (!e.target.closest?.('[name="checkout"], a[href*="/checkout"], .cart__checkout-button')) return;

      setTimeout(async () => {
        // If the redirect worked, the page is gone or hidden.
        if (document.visibilityState !== "visible") return;

        const cart = await fetch("/cart.js").then((r) => r.json()).catch(() => null);
        report("BSS checkout stuck", "error", {
          stage: diag.requests.at(-1)?.state ?? "request-never-sent",
          requests: diag.requests,
          bssLoaded: typeof window.BSS_B2B !== "undefined",
          checkoutUrlDefined: typeof window.bssB2bCheckoutUrl === "string",
          customerId: window.__st?.cid ?? null,
          cartToken: cart?.token,
          itemCount: cart?.item_count,
          totalPrice: cart?.total_price,
          url: location.href,
          userAgent: navigator.userAgent,
        });
      }, WATCHDOG_MS);
    },
    true
  );
})();
